/**
 * A agenda da clínica (ADR-0028): cada parte da tela da recepção (buscar
 * paciente, vagas, agendar…) → a operação SQL que a atende nesta organização.
 *
 * Os nomes são os da recepção, e não os da finalidade no código. O `Record`
 * por `FinalidadeDaAgenda` obriga a parte nova a ganhar nome aqui.
 */

import {
  ESCRITAS_DA_AGENDA,
  ErroDaApi,
  PARAMETROS_DA_AGENDA,
  type FinalidadeDaAgenda,
  type OperacaoDaApi,
} from '@cliqcrm/contracts';

export interface ParteDaAgenda {
  readonly parte: FinalidadeDaAgenda;
  readonly nome: string;
  /** O que a parte faz, dito para quem liga a operação. */
  readonly ajuda: string;
  readonly grava: boolean;
  readonly parametros: readonly string[];
}

const DITO: Readonly<Record<FinalidadeDaAgenda, { nome: string; ajuda: string }>> = {
  buscar_paciente: { nome: 'Buscar paciente pelo CPF', ajuda: 'Devolve os pacientes daquele CPF.' },
  buscar_paciente_por_telefone: {
    nome: 'Buscar paciente pelo telefone',
    ajuda: 'O telefone da conversa, com DDD e sem o 55.',
  },
  buscar_paciente_por_termo: {
    nome: 'Buscar paciente pelo nome ou código',
    ajuda: 'Nome, sobrenome ou código, como a recepção digita.',
  },
  ler_paciente: { nome: 'Ler o paciente', ajuda: 'O cadastro de um paciente pelo código.' },
  historico_paciente: {
    nome: 'Consultas do paciente',
    ajuda: 'As consultas que o card do paciente mostra (as 5 mais recentes).',
  },
  ultimas_vezes_paciente: {
    nome: 'Últimas vezes do paciente',
    ajuda: 'Os atendimentos, com movimento, procedimentos, convênio e clínica.',
  },
  exames_paciente: {
    nome: 'Exames do paciente',
    ajuda: 'Os exames que o paciente fez, para o prontuário: data, exame e médico.',
  },
  detalhes_dos_atendimentos: {
    nome: 'Detalhes dos atendimentos',
    ajuda:
      'O que cada card do funil é: serviço, médico, quando agendou, o dia marcado e o valor, pelas referências das rotinas.',
  },
  vagas: { nome: 'Vagas do médico', ajuda: 'Os horários livres do médico entre duas datas.' },
  bloqueios: { nome: 'Bloqueios da agenda', ajuda: 'Os períodos em que o médico não atende.' },
  medicos: { nome: 'Médicos', ajuda: 'Todos os médicos, com código e nome.' },
  medicos_por_especialidade: {
    nome: 'Médicos de uma especialidade',
    ajuda: 'Os médicos que atendem a especialidade escolhida.',
  },
  especialidades: { nome: 'Especialidades', ajuda: 'Todas as especialidades, com código e nome.' },
  convenios: { nome: 'Convênios', ajuda: 'Os convênios que a clínica atende, com código e nome.' },
  planos: { nome: 'Planos do convênio', ajuda: 'Os planos ativos do convênio escolhido.' },
  origens: {
    nome: 'Origens do agendamento',
    ajuda: 'Por onde o pedido chegou: atendimento, telefone, WhatsApp, site.',
  },
  locais: { nome: 'Locais de atendimento', ajuda: 'As unidades onde a consulta acontece.' },
  cadastrar_paciente: { nome: 'Cadastrar paciente', ajuda: 'Grava um paciente novo na clínica.' },
  agendar: { nome: 'Agendar', ajuda: 'Ocupa a vaga escolhida com o paciente.' },
  liberar_vaga: {
    nome: 'Cancelar agendamento',
    ajuda: 'Devolve a vaga, com o motivo e, se pedido, o paciente na fila de espera.',
  },
  motivos_de_cancelamento: {
    nome: 'Motivos de cancelamento',
    ajuda: 'Os motivos que a clínica usa ao cancelar, com código e nome.',
  },
  precos_de_consulta: {
    nome: 'Preços das consultas',
    ajuda:
      'O preço particular da consulta de cada médico (código do médico, do procedimento, nome e preço), e o padrão da clínica sem médico.',
  },
  precos_de_procedimentos: {
    nome: 'Preços dos exames e procedimentos',
    ajuda: 'O preço particular de cada exame e procedimento (código, nome, grupo, tipo e preço).',
  },
};

/** As partes na ordem da tela: o paciente, a agenda, e as que gravam no fim. */
export const PARTES_DA_AGENDA: readonly ParteDaAgenda[] = (
  Object.keys(PARAMETROS_DA_AGENDA) as FinalidadeDaAgenda[]
)
  .map((parte) => ({
    parte,
    ...DITO[parte],
    grava: ESCRITAS_DA_AGENDA.includes(parte),
    parametros: PARAMETROS_DA_AGENDA[parte],
  }))
  .sort((a, b) => Number(a.grava) - Number(b.grava));

/**
 * As operações que podem atender a parte: do mesmo tipo (leitura ou escrita).
 * As não publicadas ficam de fora, menos a que já está ligada: ela aparece
 * para a pessoa ver o que está lá, e o servidor recusa ao salvar.
 */
export function operacoesDaParte(
  parte: ParteDaAgenda,
  operacoes: readonly OperacaoDaApi[],
  ligada: string | undefined,
): OperacaoDaApi[] {
  return operacoes
    .filter((op) => op.tipo === (parte.grava ? 'escrita' : 'leitura'))
    .filter((op) => op.situacao === 'publicada' || op.id === ligada)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** O mapa a gravar: só as partes com operação escolhida. */
export function mapaParaGravar(rascunho: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(Object.entries(rascunho).filter(([, id]) => id !== ''));
}

/** O rascunho mudou em relação ao que está gravado? */
export function mudou(
  rascunho: Readonly<Record<string, string>>,
  gravado: Readonly<Record<string, string>>,
): boolean {
  const a = mapaParaGravar(rascunho);
  const chaves = new Set([...Object.keys(a), ...Object.keys(gravado)]);
  return [...chaves].some((chave) => a[chave] !== gravado[chave]);
}

/**
 * Os problemas que o servidor apontou, por parte (`operacoes.<parte>`), e os
 * que valem para o mapa inteiro (conexões diferentes).
 */
export function problemasDoMapa(erro: unknown): {
  porParte: Record<string, string>;
  geral: string[];
} {
  const porParte: Record<string, string> = {};
  const geral: string[] = [];
  if (!(erro instanceof ErroDaApi)) return { porParte, geral };
  for (const falha of erro.problema.errors ?? []) {
    const parte = falha.campo.startsWith('operacoes.') ? falha.campo.slice(10) : null;
    if (parte) porParte[parte] = falha.problema;
    else geral.push(falha.problema);
  }
  return { porParte, geral };
}
